import React, { useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, RefreshControl, Alert, ActivityIndicator, StyleSheet } from 'react-native';
import { Header } from '../../components/Header';
import { useGetNotificationLogsQuery, useRetryNotificationMutation } from '../../store/api';
import { useTheme } from '../../theme/ThemeContext';
import { NotificationType, NotificationStatus } from '../../types';
import { COLORS, getThemeStyles, globalStyles } from '../../theme/designSystem';

export const NotificationLogsScreen = () => {
  const { data: logs = [], isLoading, refetch } = useGetNotificationLogsQuery();
  const [retryApi, { isLoading: isRetrying }] = useRetryNotificationMutation();
  const [retryingLogId, setRetryingLogId] = useState<string | null>(null);
  const { isDark } = useTheme();
  const theme = getThemeStyles(isDark);

  const handleRetry = async (logId: string) => {
    try {
      setRetryingLogId(logId);
      await retryApi(logId).unwrap();
      Alert.alert('Retry Success', 'Notification attempt completed and status updated.');
      refetch();
    } catch (err: any) {
      Alert.alert('Retry Error', err?.data?.message || 'Failed to retry notification.');
    } finally {
      setRetryingLogId(null);
    }
  };

  return (
    <View style={[globalStyles.container, { backgroundColor: theme.bg }]}>
      <Header title="Notification Audit Logs" subtitle="WhatsApp Cloud API & Email Nodemailer dispatch logs" />

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 32 }}
        refreshControl={<RefreshControl refreshing={isLoading} onRefresh={refetch} />}
      >
        {logs.length === 0 ? (
          <View style={[globalStyles.card, { backgroundColor: theme.cardBg, borderColor: theme.border, alignItems: 'center', marginVertical: 20 }]}>
            <Text style={{ fontSize: 32, marginBottom: 8 }}>📨</Text>
            <Text style={[styles.emptyTitle, { color: theme.textPrimary }]}>
              No notification audit logs recorded yet
            </Text>
            <Text style={[styles.emptySubtitle, { color: theme.textSecondary }]}>
              Logs will appear automatically when Master Admin approves meeting requests.
            </Text>
          </View>
        ) : (
          logs.map((log) => {
            const isThisLogRetrying = retryingLogId === log.id && isRetrying;
            const isEmail = log.type === NotificationType.EMAIL;
            return (
              <View
                key={log.id}
                style={[styles.logCard, { backgroundColor: theme.cardBg, borderColor: theme.border }]}
              >
                <View style={styles.topRow}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                    <Text style={{ fontSize: 18, marginRight: 8 }}>{isEmail ? '✉️' : '💬'}</Text>
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.logType, { color: theme.textPrimary }]}>
                        {log.type} DISPATCH
                      </Text>
                      <Text style={[styles.recipient, { color: theme.textSecondary }]}>{log.recipient}</Text>
                    </View>
                  </View>

                  <View
                    style={[
                      styles.statusBadge,
                      {
                        backgroundColor: log.status === NotificationStatus.SENT ? COLORS.successBg : COLORS.dangerBg,
                        borderColor: log.status === NotificationStatus.SENT ? COLORS.success : COLORS.danger,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.statusBadgeText,
                        { color: log.status === NotificationStatus.SENT ? COLORS.success : COLORS.danger },
                      ]}
                    >
                      {log.status}
                    </Text>
                  </View>
                </View>

                {log.meeting && (
                  <View style={[styles.meetingBox, { backgroundColor: isDark ? '#0F172A' : '#F1F5F9', borderColor: theme.border }]}>
                    <Text style={[styles.visitorName, { color: theme.textPrimary }]}>
                      Visitor: {log.meeting.visitorName}
                    </Text>
                    <Text style={[styles.slotInfo, { color: theme.textSecondary }]}>
                      Slot: {log.meeting.meetingDate} @ {log.meeting.meetingTime}
                    </Text>
                  </View>
                )}

                <Text style={styles.sentAtText}>
                  Sent At: {new Date(log.sentAt).toLocaleString()}
                </Text>

                {log.status === NotificationStatus.FAILED && (
                  <TouchableOpacity
                    onPress={() => handleRetry(log.id)}
                    disabled={isThisLogRetrying}
                    style={styles.retryBtn}
                  >
                    {isThisLogRetrying ? (
                      <ActivityIndicator color={COLORS.warning} />
                    ) : (
                      <Text style={styles.retryText}>🔄 Retry Notification Dispatch</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>
            );
          })
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  emptyTitle: {
    fontSize: 14,
    fontWeight: '700',
  },
  emptySubtitle: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: 4,
  },
  logCard: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    marginBottom: 12,
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  logType: {
    fontSize: 13,
    fontWeight: '800',
  },
  recipient: {
    fontSize: 11,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  statusBadgeText: {
    fontSize: 10,
    fontWeight: '800',
  },
  meetingBox: {
    padding: 10,
    borderRadius: 10,
    borderWidth: 1,
    marginVertical: 6,
  },
  visitorName: {
    fontSize: 11,
    fontWeight: '700',
  },
  slotInfo: {
    fontSize: 10,
    marginTop: 2,
  },
  sentAtText: {
    fontSize: 10,
    color: '#94A3B8',
    marginTop: 4,
  },
  retryBtn: {
    marginTop: 10,
    paddingVertical: 10,
    backgroundColor: COLORS.warningBg,
    borderColor: COLORS.warning,
    borderWidth: 1,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  retryText: {
    color: COLORS.warning,
    fontSize: 11,
    fontWeight: '800',
  },
});
